/**
 * API Configuration
 *
 * Reads the backend API URL from environment variables.
 * Set REACT_APP_API_URL in .env file to configure the backend URL.
 *
 * Examples:
 *   - Local development: REACT_APP_API_URL=http://localhost:8000
 *   - LAN access: REACT_APP_API_URL=http://192.168.1.100:8000
 *   - WAN access: REACT_APP_API_URL=http://your-public-ip:8000
 *   - Production: REACT_APP_API_URL=https://api.yourdomain.com
 */

// Default to localhost:8000 if not specified
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Remove trailing slash if present
const normalizedUrl = API_BASE_URL.endsWith('/')
  ? API_BASE_URL.slice(0, -1)
  : API_BASE_URL;

export const API_URL = normalizedUrl;

/**
 * WebSocket URL
 *
 * Derives WebSocket URL from API URL or uses explicit REACT_APP_WS_URL.
 * Automatically converts http:// to ws:// and https:// to wss://
 *
 * Examples:
 *   - http://localhost:8000 -> ws://localhost:8000
 *   - https://api.example.com -> wss://api.example.com
 */
export const WS_URL = (() => {
  // Explicit override takes priority
  if (process.env.REACT_APP_WS_URL) {
    const wsUrl = process.env.REACT_APP_WS_URL;
    return wsUrl.endsWith('/') ? wsUrl.slice(0, -1) : wsUrl;
  }
  // Auto-derive from API URL: http -> ws, https -> wss
  return API_URL.replace(/^http/, 'ws');
})();

// Helper function to build full API URLs
export const apiUrl = (path) => {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_URL}${normalizedPath}`;
};

// Helper function to build WebSocket URLs
export const wsUrl = (path) => {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${WS_URL}${normalizedPath}`;
};

// Check if running in production
export const IS_PRODUCTION = process.env.NODE_ENV === 'production';
export const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

// Export for debugging
export const getApiConfig = () => ({
  baseUrl: API_URL,
  wsUrl: WS_URL,
  environment: process.env.NODE_ENV,
  isProduction: IS_PRODUCTION,
  configuredApiUrl: process.env.REACT_APP_API_URL || '(using default)',
  configuredWsUrl: process.env.REACT_APP_WS_URL || '(auto-derived from API_URL)'
});

// Log configuration in development
if (IS_DEVELOPMENT) {
  console.log('API Configuration:', getApiConfig());
}

export default API_URL;
